import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@mlm/ui';
import { Plus, Trash2 } from 'lucide-react';
import { listCompPlanConfigs, publishCompPlanConfig, type CompPlanConfigRow } from '../../lib/api';
import type {
  RealtimeUnilevelConfig,
  RealtimeUnilevelProduct,
} from '../compplan/realtime-unilevel-editor';

const PLAN_CODE = 'realtime_unilevel';

/** Latest-version config row for the realtime_unilevel plan, or null. */
function latestConfig(rows: CompPlanConfigRow[]): CompPlanConfigRow | null {
  return rows
    .filter((r) => r.planCode === PLAN_CODE)
    .reduce<CompPlanConfigRow | null>(
      (best, r) => (!best || r.version > best.version ? r : best),
      null,
    );
}

/**
 * Per-product view over the global `realtime_unilevel` comp config. The plan
 * stores one global blob `{ products: [{ product_id, level_amounts }] }`; here we
 * edit just this product's `level_amounts` and splice it back into the full blob,
 * publishing a new config version (configs are versioned, never mutated).
 *
 * Only rendered for super_admin when the plan is enabled (gated by the caller).
 */
export function ProductRealtimeUnilevelEditor({ productId }: { productId: string }) {
  const qc = useQueryClient();
  const configsQ = useQuery({
    queryKey: ['admin-compplan-configs'],
    queryFn: listCompPlanConfigs,
  });

  const current = useMemo(() => latestConfig(configsQ.data ?? []), [configsQ.data]);
  const fullConfig = (current?.config as RealtimeUnilevelConfig | undefined) ?? { products: [] };
  const existing = fullConfig.products?.find((p) => p.product_id === productId);

  // Local draft of THIS product's per-level amounts. `null` = follow server.
  const [draft, setDraft] = useState<number[] | null>(null);
  const levels = draft ?? existing?.level_amounts ?? [0];

  const save = useMutation({
    mutationFn: () => {
      const others = (fullConfig.products ?? []).filter((p) => p.product_id !== productId);
      // Drop the row entirely when there's nothing to pay (all-zero / empty).
      const keep = levels.some((a) => a > 0);
      const nextProducts: RealtimeUnilevelProduct[] = keep
        ? [...others, { product_id: productId, level_amounts: levels }]
        : others;
      return publishCompPlanConfig({
        planCode: PLAN_CODE,
        config: { ...fullConfig, products: nextProducts },
      });
    },
    meta: { successMessage: 'Unilevel pricing saved.' },
    onSuccess: () => {
      setDraft(null);
      qc.invalidateQueries({ queryKey: ['admin-compplan-configs'] });
    },
  });

  function setAmount(level: number, amount: number) {
    const next = [...levels];
    next[level] = amount;
    setDraft(next);
  }
  function addLevel() {
    setDraft([...levels, 0]);
  }
  function removeLevel(level: number) {
    setDraft(levels.filter((_, i) => i !== level));
  }

  if (configsQ.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-muted-foreground">
        Fires the instant a member buys this product (no maintenance / qualification). Pays a FIXED
        cash amount per level: Level 0 is the buyer's own cashback; Levels 1+ pay the sponsor
        uplines. Amounts scale by quantity. A level with no upline is unpaid. Saving here updates
        the shared Realtime Unilevel comp-plan config.
      </p>

      <table className="w-full overflow-hidden rounded-md border text-sm">
        <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
          <tr>
            <th className="w-40 px-3 py-2 font-medium">Level</th>
            <th className="px-3 py-2 font-medium">Amount</th>
            <th className="w-12 px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {levels.map((amt, lvl) => (
            <tr key={lvl} className="border-t">
              <td className="px-3 py-2 text-xs">
                {lvl === 0 ? (
                  <span className="font-mono">L0 — buyer cashback</span>
                ) : (
                  <span className="font-mono">L{lvl}</span>
                )}
              </td>
              <td className="px-3 py-2">
                <input
                  type="number"
                  className="h-8 w-36 rounded-md border bg-background px-2 text-sm"
                  value={amt}
                  step={0.01}
                  min={0}
                  onChange={(e) => setAmount(lvl, Number(e.target.value) || 0)}
                />
              </td>
              <td className="px-3 py-2">
                {levels.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeLevel(lvl)}
                    className="text-muted-foreground transition-colors hover:text-destructive"
                    aria-label={`Remove level ${lvl}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" onClick={addLevel}>
          <Plus className="mr-1 h-3 w-3" />
          Add level
        </Button>
        <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save unilevel'}
        </Button>
        {save.isSuccess && draft === null && <span className="text-xs text-success">Saved</span>}
        {save.error != null && <span className="text-xs text-destructive">Save failed</span>}
      </div>
    </div>
  );
}
